"use client";

import { useEffect, useId, useImperativeHandle, useRef, useState, type Ref } from "react";
import { useTheme } from "next-themes";
import { getTossProductProfile } from "@/lib/toss-product";
import { createTossCheckout, type TossCheckoutController } from "@/lib/toss-checkout";
import type { TossCheckout } from "@/lib/payment-provider";

export interface TossPaymentWidgetHandle {
  requestPayment: (checkout: TossCheckout) => Promise<void>;
}
interface Props {
  ref: Ref<TossPaymentWidgetHandle>;
  amount: number;
  productId: string;
  customerKey: string;
  onReadyChange: (ready: boolean) => void;
}

export default function TossPaymentWidget({ ref, amount, productId, customerKey, onReadyChange }: Props) {
  const { resolvedTheme } = useTheme();
  const widgetTheme = resolvedTheme === "dark" && getTossProductProfile(productId).paymentDarkVariantKey ? "dark" : "light";
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const controller = useRef<TossCheckoutController | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{ key: string; error?: string } | null>(null);
  const stateKey = `${productId}:${customerKey}:${amount}:${widgetTheme}:${attempt}`;
  const current = result?.key === stateKey ? result : null;
  useImperativeHandle(ref, () => ({
    async requestPayment(checkout) {
      if (!controller.current) throw new Error("결제 수단을 불러오는 중입니다.");
      await controller.current.requestPayment(checkout);
    },
  }), []);

  useEffect(() => {
    let active = true;
    onReadyChange(false);
    if (!resolvedTheme) return;
    const session = createTossCheckout({
      clientKey: process.env.NEXT_PUBLIC_TOSS_CLIENT_KEY || "",
      customerKey, amount, productId, theme: widgetTheme,
      paymentMethodsSelector: `#toss-methods-${id}`,
      agreementSelector: `#toss-agreement-${id}`,
    });
    controller.current = session;
    session.ready.then(() => {
      if (!active) return;
      setResult({ key: stateKey });
      onReadyChange(true);
    }).catch((error: unknown) => {
      if (!active) return;
      // SDK 원문에는 키나 결제 정보가 포함될 수 있어 오류 코드만 기록한다.
      const code = error && typeof error === "object" && "code" in error ? error.code : null;
      const safeCode = typeof code === "string" && /^[A-Z][A-Z0-9_]{0,80}$/.test(code) ? code : "UNKNOWN";
      console.error(`Toss widget initialization failed: ${safeCode}`);
      setResult({ key: stateKey, error: "결제 수단을 불러오지 못했습니다. 다시 시도해주세요." });
      onReadyChange(false);
    });
    return () => {
      active = false;
      controller.current = null;
      onReadyChange(false);
      void session.destroy().catch(() => {});
    };
  }, [amount, productId, customerKey, id, onReadyChange, stateKey, widgetTheme, resolvedTheme]);

  return (
    <section aria-label="결제 수단 및 약관" className="min-w-0 space-y-3">
      {!current && <p role="status" className="py-4 text-sm text-muted-foreground">결제 수단을 불러오는 중입니다…</p>}
      {current?.error && (
        <div role="alert" className="rounded-lg border border-border bg-muted p-4 text-sm text-foreground">
          <p>{current.error}</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)} className="mt-2 min-h-11 rounded-md px-3 font-medium underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">다시 불러오기</button>
        </div>
      )}
      {/* iframe 내부는 토스 테마 UI가 담당한다. 미설정 시 밝은 UI와 외곽을 함께 유지한다. */}
      <div className={`overflow-hidden rounded-xl border border-border ${widgetTheme === "dark" ? "bg-background text-foreground [color-scheme:dark]" : "bg-white text-gray-950 [color-scheme:light]"}`} data-payment-theme={widgetTheme}>
        <div id={`toss-methods-${id}`} />
        <div id={`toss-agreement-${id}`} />
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        현금영수증은 계좌이체·무통장입금 결제 과정에서 신청할 수 있습니다. 무통장입금은 안내된 기한 내 입금 확인 후 이용할 수 있습니다.
      </p>
    </section>
  );
}
